import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = 'https://tribaltech-backend.onrender.com';

// Videos shorter than this wait for the buyer-specific watermark
// to finish before we let them play — the pipeline usually beats
// the buyer to the play button on short clips, and avoiding the
// mid-play URL swap is a nicer UX. Longer videos serve the
// creator-watermarked base immediately and swap silently when the
// buyer copy lands.
const SHORT_VIDEO_MAX_SECONDS = 600; // 10 minutes

function dollars(cents) { return '$' + (cents / 100).toFixed(2); }

export default function PpvPublic() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [buying, setBuying] = useState(false);

  // Post-purchase state
  const [claimedEmail, setClaimedEmail] = useState(null);
  const [purchaseStatus, setPurchaseStatus] = useState(null); // null | 'watermarking' | 'delivered' | etc.
  const [purchaseId, setPurchaseId]   = useState(null);
  const [watchUrl, setWatchUrl]       = useState(null);
  const [watchLayer, setWatchLayer]   = useState(null); // 'base' | 'buyer'
  const [claimErr, setClaimErr]       = useState('');
  const [watermarkErr, setWatermarkErr] = useState('');
  const pollRef = useRef(null);

  const purchaseSuccess = params.get('purchase') === 'success';
  const sessionId       = params.get('session_id');

  useEffect(() => {
    axios.get(`${API}/api/ppv/listings/${id}`)
      .then(r => setListing(r.data.listing))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
    // Fire-and-forget view tracker. Skip on the Stripe-success
    // redirect so the buyer doesn't double-count their own view.
    if (!purchaseSuccess) {
      axios.post(`${API}/api/ppv/listings/${id}/view`).catch(() => {});
    }
  }, [id, purchaseSuccess]);

  // Kick off the claim flow once we know we're a success redirect
  useEffect(() => {
    if (!purchaseSuccess || !sessionId) return;
    (async () => {
      try {
        const r = await axios.post(`${API}/api/ppv/claim`, { session_id: sessionId });
        localStorage.setItem('vault_token', r.data.jwt);
        localStorage.setItem('vault_email', r.data.email);
        setClaimedEmail(r.data.email);
        setPurchaseStatus(r.data.purchase?.status || 'pending');
        setPurchaseId(r.data.purchase?.id || null);
      } catch (e) {
        setClaimErr(e.response?.data?.error || e.message);
      }
    })();
  }, [purchaseSuccess, sessionId]);

  // Once we have a purchase id, poll the vault download endpoint
  // until it returns a URL (status flips to 'delivered' server-side
  // when wavmark_api finishes).
  useEffect(() => {
    if (!purchaseId) return;
    const token = localStorage.getItem('vault_token');
    if (!token) return;

    async function tick() {
      try {
        const r = await axios.get(`${API}/api/vault/purchases/${purchaseId}/download`, {
          params: { mode: 'watch' },
          headers: { Authorization: 'Bearer ' + token },
        });
        if (r.data?.url) {
          const duration = listing?.duration_seconds || 0;
          const isShort  = duration > 0 && duration < SHORT_VIDEO_MAX_SECONDS;

          if (r.data.layer === 'buyer') {
            // Buyer layer is the final state either way — show it
            // and stop polling.
            setWatchUrl(r.data.url);
            setWatchLayer('buyer');
            setPurchaseStatus('delivered');
            if (pollRef.current) clearInterval(pollRef.current);
          } else if (r.data.layer === 'base') {
            // Base is available. Short videos: wait, don't play
            // the base — the buyer copy is usually seconds away
            // and we want to avoid a mid-play URL swap. Long
            // videos: play the base immediately and keep polling
            // so we can swap silently when the buyer copy lands.
            if (!isShort) {
              setWatchUrl(prev => (prev && watchLayer === 'base') ? prev : r.data.url);
              setWatchLayer('base');
            }
            const bs = r.data.buyer_status;
            if (bs) setPurchaseStatus(bs);
            if (bs === 'failed') {
              if (r.data.watermark_error) setWatermarkErr(r.data.watermark_error);
              if (pollRef.current) clearInterval(pollRef.current);
            }
          }
        }
      } catch (e) {
        // 409 = nothing playable yet (upload pipeline still running
        // or failed) — keep polling unless terminal.
        if (e.response?.status === 409) {
          const s = e.response.data?.status;
          if (s) setPurchaseStatus(s);
          if (e.response.data?.watermark_error) {
            setWatermarkErr(e.response.data.watermark_error);
          }
          if (e.response.data?.base_watermark_status === 'failed' || s === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current);
          }
        } else {
          console.warn('[PPV claim] download poll error:', e.message);
        }
      }
    }
    tick();
    pollRef.current = setInterval(tick, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseId, listing?.duration_seconds]);

  async function buy() {
    if (!email) { alert('Enter your email so we can send the download link.'); return; }
    setBuying(true);
    try {
      const affiliate_code = sessionStorage.getItem('vault_ref') || undefined;
      const r = await axios.post(`${API}/api/ppv/listings/${id}/purchase`, { email, affiliate_code });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      throw new Error('no checkout URL returned');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
      setBuying(false);
    }
  }

  if (loading) return <div style={pageStyle}><div style={{ color:'#888' }}>Loading…</div></div>;
  if (error || !listing) return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={{ color:'#fff', fontSize: 22 }}>Not found</h1>
        <p style={{ color:'#94a3b8' }}>{error || 'This listing doesn\'t exist or has been removed.'}</p>
      </div>
    </div>
  );

  // ── Post-purchase view ──
  if (purchaseSuccess) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color:'#888', letterSpacing: 2, textTransform:'uppercase', marginBottom: 6 }}>
            Creator Protect · PPV
          </div>
          <h1 style={{ color:'#fff', fontSize: 26, margin: '6px 0 10px' }}>{listing.title}</h1>
          <div style={{ display:'inline-block', padding:'4px 10px', borderRadius: 4, background:'rgba(74,222,128,0.12)', color:'#4ade80', fontSize: 12, marginBottom: 20 }}>
            ✓ Payment received{claimedEmail ? ` · ${claimedEmail}` : ''}
          </div>

          <div style={{ background:'#0a0a0a', borderRadius: 12, overflow:'hidden', marginBottom: 20, aspectRatio:'16/9', position:'relative' }}>
            {watchUrl ? (
              <>
                <video key={watchUrl} src={watchUrl} controls autoPlay playsInline
                       style={{ width:'100%', height:'100%', display:'block' }} />
                {watchLayer === 'base' && purchaseStatus !== 'failed' && (
                  <div style={{
                    position:'absolute', top: 10, right: 10,
                    background:'rgba(124,58,237,0.9)', color:'#fff',
                    fontSize: 11, padding:'4px 10px', borderRadius: 4, letterSpacing: 0.5,
                  }}>
                    Personalizing your copy…
                  </div>
                )}
              </>
            ) : (
              <div style={{ color:'#888', textAlign:'center', padding: '80px 20px' }}>
                {purchaseStatus === 'failed' ? (
                  <>
                    <div style={{ color:'#f87171', fontSize: 14, marginBottom: 8 }}>
                      Watermarking failed.
                    </div>
                    <div style={{ fontSize: 12, color:'#666' }}>
                      Email support@creatorprotect.io — we've got a record of your purchase.
                    </div>
                    {watermarkErr && (
                      <div style={{ fontSize: 11, color:'#555', marginTop: 12, fontFamily:'monospace', textAlign:'left', maxHeight: 80, overflow:'auto', padding: 8, background:'#111', borderRadius: 4 }}>
                        {watermarkErr}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ color:'#a78bfa', fontSize: 14, marginBottom: 8 }}>Preparing your copy…</div>
                    <div style={{ fontSize: 12, color:'#666' }}>
                      We're embedding a personal watermark. This usually takes under a minute.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {claimErr && (
            <div style={{ padding: 12, background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius: 8, color:'#f87171', fontSize: 13, marginBottom: 16 }}>
              Couldn't auto-unlock: {claimErr}. Try your vault — we've emailed you a sign-in link.
            </div>
          )}

          <div style={{ display:'flex', gap: 10, flexWrap:'wrap' }}>
            <Link to="/vault/my-purchases" style={linkBtn}>Open my vault</Link>
            {watchUrl && (
              <a href={watchUrl} download style={linkBtnSecondary}>Download</a>
            )}
          </div>

          <div style={{ marginTop: 20, fontSize: 11, color:'#666', textAlign:'center', lineHeight: 1.6 }}>
            This browser will remember you — no login needed.<br/>
            On a different device, enter your email at <Link to="/vault/login" style={{ color:'#a78bfa' }}>/vault/login</Link>.
          </div>
        </div>
      </div>
    );
  }

  // ── Pre-purchase view (listing page) ──
  const isPublished = listing.status === 'published';
  const previewReady = listing.preview_path && listing.preview_url;

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: 11, color:'#888', letterSpacing: 2, textTransform:'uppercase', marginBottom: 6 }}>
          Creator Protect · PPV
        </div>
        <h1 style={{ color:'#fff', fontSize: 26, margin: '6px 0 18px' }}>{listing.title}</h1>

        <div style={{ background:'#0a0a0a', borderRadius: 12, overflow:'hidden', marginBottom: 20, aspectRatio:'16/9', position:'relative' }}>
          {previewReady ? (
            <video src={listing.preview_url} controls playsInline
                   style={{ width:'100%', height:'100%', display:'block' }} />
          ) : (
            <div style={{ color:'#666', textAlign:'center', padding: '80px 20px' }}>
              Preview is still being generated — refresh in a minute.
            </div>
          )}
          <div style={{ position:'absolute', bottom: 8, right: 10, fontSize: 11, color:'rgba(255,255,255,0.7)', background:'rgba(0,0,0,0.5)', padding:'3px 8px', borderRadius: 4 }}>
            30-sec preview · full video unlocks after purchase
          </div>
        </div>

        {listing.description && (
          <p style={{ color:'#cbd5e1', lineHeight: 1.6, marginBottom: 24 }}>{listing.description}</p>
        )}

        <div style={{ display:'flex', alignItems:'baseline', gap: 12, marginBottom: 20 }}>
          <div style={{ fontSize: 32, color:'#fff', fontWeight: 600 }}>{dollars(listing.price_cents)}</div>
          <div style={{ fontSize: 13, color:'#888' }}>
            USD · one-time payment
            {typeof listing.total_views === 'number' && listing.total_views > 0 && (
              <> · {listing.total_views.toLocaleString()} {listing.total_views === 1 ? 'view' : 'views'}</>
            )}
          </div>
        </div>

        {!isPublished && (
          <div style={{ padding: 12, background:'rgba(251,191,36,0.1)', border:'1px solid rgba(251,191,36,0.3)', borderRadius: 8, color:'#fbbf24', fontSize: 13, marginBottom: 16 }}>
            This listing isn't published yet — checkout is disabled.
          </div>
        )}

        <div style={{ marginBottom: 10 }}>
          <label style={{ display:'block', fontSize: 12, color:'#94a3b8', marginBottom: 6 }}>Your email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={!isPublished || buying}
            style={{ width:'100%', padding: 12, borderRadius: 8, background:'#0a0a0a', border:'1px solid #222', color:'#fff', fontSize: 14, boxSizing:'border-box' }}
          />
        </div>

        <button
          onClick={buy}
          disabled={!isPublished || buying}
          style={{
            width:'100%', padding: 14, borderRadius: 8, border:'none',
            background: isPublished ? '#7c3aed' : '#333',
            color:'#fff', fontSize: 15, fontWeight: 600,
            cursor: isPublished ? 'pointer' : 'not-allowed', opacity: isPublished ? 1 : 0.6,
          }}
        >
          {buying ? 'Redirecting to checkout…' : `Buy for ${dollars(listing.price_cents)}`}
        </button>

        <div style={{ marginTop: 14, fontSize: 11, color:'#555', textAlign:'center' }}>
          Secured by Stripe · Creator Protect
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color:'#666', textAlign:'center' }}>
          Already bought something? <Link to="/vault/login" style={{ color:'#a78bfa' }}>Open your vault →</Link>
        </div>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #0f0f1e 0%, #0a0a0f 100%)',
  display:'flex', alignItems:'center', justifyContent:'center',
  padding: '40px 20px',
};
const cardStyle = {
  background: '#111', borderRadius: 16, padding: 32,
  maxWidth: 640, width: '100%',
  border: '1px solid #222',
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};
const linkBtn = {
  flex: 1, textAlign:'center', padding: '12px 16px', borderRadius: 8,
  background:'#7c3aed', color:'#fff', textDecoration:'none',
  fontWeight: 600, fontSize: 14, minWidth: 150,
};
const linkBtnSecondary = {
  flex: 1, textAlign:'center', padding: '12px 16px', borderRadius: 8,
  background:'#1a1a1a', color:'#fff', textDecoration:'none',
  fontWeight: 600, fontSize: 14, border:'1px solid #333', minWidth: 150,
};
